/**
 * Générateur — Automatisme : construire le symétrique d'un point par
 * demi-tour.
 * Attendu ID : 1119 | Niveau : 4ème | Domaine : Transformations
 * Rubrique : AUTOMATISME (programme 2026, Transformations 4e) — rituel court.
 * Labels (cartes exerciseur) :
 *   - Placer le symétrique           (interactif placer_points_2d : O et M placés, l'élève place M' ;
 *                                     QCM sur figure : quatre candidats, un seul symétrique)
 *   - Vrai ou faux ?                 (V/F sur figure avec O, M et un point N)
 *   - Le demi-tour : vrai ou faux ?  (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-09) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Transformations 4e, automatisme « Construire le
 * symétrique d'un point par demi-tour » + grille P1-P12. P1 : CM2 introduit
 * (symétrie axiale), 6e consolidé (symétrique d'un point par symétrie axiale,
 * médiatrice), 5e étendu (demi-tour défini, O milieu de [MM']), 4e
 * automatisme. P2 : sens installés — rotation d'un demi-tour, O milieu, « le
 * déplacement de M à O se poursuit de O à M' » ; non installés (distracteurs)
 * — symétrie axiale, quart de tour, s'arrêter en O, milieu de [MO]. P3 :
 * quadrillage à coordonnées entières de −5 à 5, déplacements de 1 à 4
 * carreaux. P5 : quadrillage ; repère orthogonal pour le placement
 * interactif (le composant l'impose ; les coordonnées sont un support). P6 :
 * placement au clic. P7 : pas de formule de coordonnées : on compte les
 * carreaux. P8 : demi-tour, symétrie centrale, centre, symétrique, image,
 * milieu. P9 : symétrie axiale sur quadrillage (5e), lecture de coordonnées
 * (4e), milieu (6e). P10 : constat. P11 : confondre demi-tour et symétrie
 * axiale ; s'arrêter en O ; n'inverser qu'une direction. P12 : rémanence
 * forte.
 * INVARIANTS :
 *   - M' = 2O − M en entiers ; O, M, M' et les candidats restent dans la
 *     grille ; toute figure porte data-points pour les solveurs ;
 *   - leurres géométriques distincts, jamais confondus avec O ou M ; V/F :
 *     pas de crochets, mixité 1-3 faux, familles exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, basePayload,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 1119;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PLACER = 'Placer le symétrique';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Le demi-tour : vrai ou faux ?';

const B = 5;                                                       // grille de −5 à 5
const dans = (p) => Math.abs(p.x) <= B && Math.abs(p.y) <= B;
const egal = (p, q) => p.x === q.x && p.y === q.y;
const fr = (n) => String(n);                                       // entiers, signe « - »

/** O et M sur la grille, M ≠ O, M' = 2O − M dans la grille ; sous-type selon la position de O et de M. */
const tirerOM = () => {
    for (let essai = 0; essai < 200; essai++) {
        const cas = randomChoice(['origine', 'quelconque', 'quelconque', 'ligne']);
        const O = cas === 'origine' ? { x: 0, y: 0 } : { x: randInt(-2, 2), y: randInt(-2, 2) };
        let dx = randInt(-4, 4); let dy = randInt(-4, 4);
        if (cas === 'ligne') { if (Math.random() < 0.5) dx = 0; else dy = 0; }
        if (dx === 0 && dy === 0) continue;
        if (cas !== 'ligne' && (dx === 0 || dy === 0)) continue;
        const M = { x: O.x + dx, y: O.y + dy }; const Mp = { x: O.x - dx, y: O.y - dy };
        if (!dans(M) || !dans(Mp)) continue;
        return { cas, O, M, Mp, dx, dy };
    }
    return { cas: 'origine', O: { x: 0, y: 0 }, M: { x: 3, y: 2 }, Mp: { x: -3, y: -2 }, dx: 3, dy: 2 };
};

// ─── Quadrillage SVG (sans axes : le demi-tour se lit en carreaux) ─────────
const CELL = 22; const PAD = 26; const W = 2 * B * CELL + 2 * PAD;      // marge large : les étiquettes des points du bord restent dans le cadre
const px = (x) => PAD + (x + B) * CELL; const py = (y) => PAD + (B - y) * CELL;
const grille = (points, extra = '') => {
    let g = `<rect x="0" y="0" width="${W}" height="${W}" fill="#ffffff"/>`;
    for (let v = -B; v <= B; v++) {
        g += `<line x1="${px(v)}" y1="${py(-B)}" x2="${px(v)}" y2="${py(B)}" stroke="#cbd5e1" stroke-width="1"/>`;
        g += `<line x1="${px(-B)}" y1="${py(v)}" x2="${px(B)}" y2="${py(v)}" stroke="#cbd5e1" stroke-width="1"/>`;
    }
    g += extra;
    points.forEach((p) => {
        const c = p.couleur || '#1e3a8a';
        g += `<circle cx="${px(p.x)}" cy="${py(p.y)}" r="4.5" fill="${c}"/>`;
        g += `<text x="${px(p.x) + 7}" y="${py(p.y) - 7}" font-family="system-ui,sans-serif" font-size="14" font-weight="700" fill="${c}">${p.label}</text>`;
    });
    const data = points.map((p) => `${p.label}:${p.x};${p.y}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${W}" width="${W}" height="${W}" data-points="${data}">${g}</svg>`;
};

// ─── Codes d'erreur (contenu.erreurs) ──────────────────────────────────────
/** Les points où une erreur connue envoie M : symétries d'axe vertical ou horizontal passant par O, quarts de tour de centre O. */
const erreursSymetrique = ({ O, dx, dy }) => [
    { code: 'demi_tour_pour_axiale', p: { x: O.x - dx, y: O.y + dy } },
    { code: 'demi_tour_pour_axiale', p: { x: O.x + dx, y: O.y - dy } },
    { code: 'quart_de_tour_pour_demi_tour', p: { x: O.x - dy, y: O.y + dx } },
    { code: 'quart_de_tour_pour_demi_tour', p: { x: O.x + dy, y: O.y - dx } },
];
/** Le code d'un point, s'il est seul à y mener (null sinon). */
const codeDuPoint = (erreurs, q) => { const c = [...new Set(erreurs.filter((e) => egal(e.p, q)).map((e) => e.code))]; return c.length === 1 ? c[0] : null; };

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refP = { v: null }; const refQ = { v: null };
const sig = (T) => `${T.O.x},${T.O.y}-${T.M.x},${T.M.y}`;

// ─── Carte 1 : placer le symétrique (interactif) ───────────────────────────

const construirePlacer = () => {
    const T = tirerOM();
    const payload = basePayload('association',
        `<p>Les points O et M sont placés. Place le point M', symétrique de M par le demi-tour de centre O.</p>`,
        {
            subType: 'placer_points_2d',
            repere: { xMin: -B, yMin: -B, xMax: B, yMax: B, xStep: 1, yStep: 1 },
            pointsToPlace: ["M'"],
            dejaPlaces: [{ x: T.O.x, y: T.O.y, label: 'O' }, { x: T.M.x, y: T.M.y, label: 'M' }],
            bonne_reponse: JSON.stringify({ "M'": `${fr(T.Mp.x)};${fr(T.Mp.y)}` }),
            sousType: T.cas,
        },
        opts(L_PLACER));
    payload._meta.params = { ...T, signature: sig(T) };
    // positions fausses nommées par les calculs des leurres du QCM, dans la grille, jamais en O, M ou M'
    const E = erreursSymetrique(T);
    const erreurs = [];
    E.forEach(({ p }) => {
        const valeur = `${fr(p.x)};${fr(p.y)}`; const code = codeDuPoint(E, p);
        if (code && dans(p) && ![T.O, T.M, T.Mp].some((q) => egal(q, p)) && !erreurs.some((e) => e.valeur === valeur)) erreurs.push({ code, point: "M'", valeur });
    });
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genPlacer = () => antiRepeat(construirePlacer, refP);

// ─── Carte 1b : le bon point (QCM sur figure, rattaché à « Placer le symétrique », prof 2026-09-09) ─

const construireBonQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const T = tirerOM(); const { O, M, Mp, dx, dy } = T;
        const cands = [
            { p: Mp, role: 'symetrique' },
            { p: { x: O.x - dx, y: O.y + dy }, role: 'axial_vertical' },        // symétrie d'axe vertical par O
            { p: { x: O.x + dx, y: O.y - dy }, role: 'axial_horizontal' },      // symétrie d'axe horizontal par O
            { p: { x: O.x - dy, y: O.y + dx }, role: 'quart_de_tour' },         // quart de tour
            { p: { x: O.x + dy, y: O.y - dx }, role: 'quart_de_tour_inverse' },
        ];
        const pris = [];
        cands.forEach((c) => { if (pris.length < 4 && dans(c.p) && !egal(c.p, O) && !egal(c.p, M) && !pris.some((q) => egal(q.p, c.p))) pris.push(c); });
        if (pris.length < 4 || !pris.some((c) => c.role === 'symetrique')) continue;
        const lettres = shuffle(['A', 'B', 'C', 'D']);
        const points = pris.map((c, i) => ({ ...c.p, label: lettres[i], role: c.role }));
        const bonne = points.find((p) => p.role === 'symetrique').label;
        const svg = grille([{ ...O, label: 'O', couleur: '#b45309' }, { ...M, label: 'M', couleur: '#b45309' }, ...points]);
        const payload = makeQcm(['A', 'B', 'C', 'D'], bonne, `<p>Quel point est le symétrique de M par le demi-tour de centre O ?</p>`, { ...opts(L_PLACER), elements_additionnels: imgElement(svg, 'medium') });
        // options remises dans l'ordre A, B, C, D (l'étiquette du point EST la lettre de l'option)
        payload.contenu.choix = ['A', 'B', 'C', 'D']; payload.contenu.bonne_reponse = bonne;
        payload.contenu = { ...payload.contenu, sousType: T.cas };
        payload._meta.params = { ...T, points, signature: sig(T) };
        // chaque point leurre nommé par la transformation qui le construit, quand elle est seule à y mener
        const E = erreursSymetrique(T);
        const erreurs = points.filter((p) => p.role !== 'symetrique').map((p) => ({ code: codeDuPoint(E, p), valeur: p.label })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1119 : QCM sans candidats');
};
const genBonQcm = () => antiRepeat(construireBonQcm, refQ);

// ─── Carte 2 : vrai ou faux ? (figure avec O, M et un point N) ─────────────

let dernieresLignesVf1119 = '';

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const T = tirerOM(); const { O, M, Mp, dx, dy } = T;
        // N : le symétrique (1 fois sur 2) ou un leurre géométrique
        const leurres = [{ x: O.x - dx, y: O.y + dy }, { x: O.x + dx, y: O.y - dy }, { x: O.x - dy, y: O.y + dx }].filter((p) => dans(p) && !egal(p, O) && !egal(p, M) && !egal(p, Mp));
        const N = Math.random() < 0.5 || !leurres.length ? Mp : randomChoice(leurres);
        const estSym = egal(N, Mp);
        const d2 = (P, Q) => (P.x - Q.x) ** 2 + (P.y - Q.y) ** 2;
        const alignes = (M.x - O.x) * (N.y - O.y) - (M.y - O.y) * (N.x - O.x) === 0;
        const memeCote = alignes && (M.x - O.x) * (N.x - O.x) + (M.y - O.y) * (N.y - O.y) > 0;
        const candidates = [
            { l: 'N est le symétrique de M par le demi-tour de centre O.', v: estSym, f: 'sym' },
            { l: 'O est le milieu du segment qui joint M et N.', v: estSym, f: 'milieu' },
            { l: 'N est à la même distance de O que M.', v: d2(N, O) === d2(M, O), f: 'distance' },
            { l: 'Les points M, O et N sont alignés.', v: alignes, f: 'alignes' },
            { l: 'N est du même côté de O que M.', v: memeCote, f: 'cote' },
            // axes explicites (revue 2026-09-09) : « par une symétrie axiale » sans axe était vrai pour tout N (médiatrice de [MN])
            { l: 'N est le symétrique de M par rapport à la droite verticale qui passe par O.', v: N.x === 2 * O.x - M.x && N.y === M.y, f: 'axe_v' },
            { l: 'N est le symétrique de M par rapport à la droite horizontale qui passe par O.', v: N.x === M.x && N.y === 2 * O.y - M.y, f: 'axe_h' },
        ];
        // sym et milieu ont la même vérité par construction : une seule des deux par tirage
        const pool = candidates.filter((it) => it.f !== randomChoice(['sym', 'milieu']));
        const fausses = shuffle(pool.filter((it) => !it.v)); const vraies = shuffle(pool.filter((it) => it.v));
        // nombre de faux tiré dans l'intervalle réellement possible (revue 2026-09-09 : sinon N était le symétrique 63 % du temps)
        const minFaux = Math.max(1, 4 - vraies.length); const maxFaux = Math.min(3, fausses.length);
        if (minFaux > maxFaux) continue;
        const nbFaux = randInt(minFaux, maxFaux);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4 || new Set(lignes.map((it) => it.f)).size !== 4) continue;
        // lignes à texte fixe : on interdit seulement de reproduire exactement le tirage précédent (lignes + verdicts)
        const empreinte = lignes.map((it) => `${it.l}|${it.v}`).sort().join('#');
        if (empreinte === dernieresLignesVf1119) continue;
        dernieresLignesVf1119 = empreinte;
        const svg = grille([{ ...O, label: 'O', couleur: '#b45309' }, { ...M, label: 'M' }, { ...N, label: 'N' }]);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Observe la figure. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { ...T, N, estSym };
        // N leurre accepté comme le symétrique : la transformation qui le construit ; N symétrique accepté comme image par un axe : demi-tour pris pour une symétrie axiale
        const E = erreursSymetrique(T);
        const Av = { x: 2 * O.x - M.x, y: M.y }; const Ah = { x: M.x, y: 2 * O.y - M.y };
        const codeFaux = {
            sym: codeDuPoint(E, N),
            milieu: codeDuPoint(E, N),
            axe_v: estSym && !egal(N, Ah) ? 'demi_tour_pour_axiale' : null,
            axe_h: estSym && !egal(N, Av) ? 'demi_tour_pour_axiale' : null,
        };
        const erreurs = lignes.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1119 : V/F sans tirage après 160 essais');
};

// ─── Carte 3 : le demi-tour, vrai ou faux ? (pur rappel) ───────────────────

const POOL_RAPPEL = [
    { l: 'Le symétrique de M par le demi-tour de centre O est le point M\' tel que O soit le milieu du segment joignant M et M\'.', v: true, f: 'definition' },
    { l: 'Le symétrique de M par le demi-tour de centre O est le point M\' tel que M soit le milieu du segment joignant O et M\'.', v: false, f: 'definition' },
    { l: 'Un demi-tour de centre O, c\'est une rotation d\'un demi-tour autour de O.', v: true, f: 'nature' },
    { l: 'Un demi-tour de centre O, c\'est un pliage le long d\'une droite qui passe par O.', v: false, f: 'nature', code: 'demi_tour_pour_axiale' },
    { l: 'Le demi-tour est aussi appelé symétrie centrale.', v: true, f: 'nom' },
    { l: 'Le demi-tour est aussi appelé symétrie axiale.', v: false, f: 'nom', code: 'demi_tour_pour_axiale' },
    { l: 'Le symétrique du centre O par le demi-tour de centre O est O lui-même.', v: true, f: 'centre' },
    { l: 'Le centre O n\'a pas de symétrique par le demi-tour de centre O.', v: false, f: 'centre', code: 'centre_sans_image' },
    { l: 'Un point et son symétrique par demi-tour sont à la même distance du centre.', v: true, f: 'distance' },
    { l: 'Un point et son symétrique par demi-tour sont du même côté du centre.', v: false, f: 'distance' },
    { l: 'Sur un quadrillage, pour aller de O à M\', on refait le déplacement qui va de M à O, dans le même sens.', v: true, f: 'geste' },
    { l: 'Sur un quadrillage, pour aller de O à M\', on refait le déplacement qui va de O à M.', v: false, f: 'geste' },
    { l: 'Le demi-tour et la symétrie axiale donnent toujours la même image.', v: false, f: 'axiale', code: 'demi_tour_pour_axiale' },
    { l: 'Le point M, le centre O et le symétrique M\' sont toujours alignés.', v: true, f: 'axiale' },
];

let dernieresLignesRappel1119 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1119 = [];
        if (lignes.some((it) => dernieresLignesRappel1119.includes(it.l))) continue;
        dernieresLignesRappel1119 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur le demi-tour, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier (aucun pour M milieu, « du même côté » ni le déplacement de O à M)
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1119 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    placer_placer_points: genPlacer,
    placer_qcm_unique: genBonQcm,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { placer_points: 'Placer le point', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    placer_qcm_unique: ['demi_tour_pour_axiale', 'quart_de_tour_pour_demi_tour'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
